/**
 * NavEntryLink: one row of a navigation list, the sidebar's and the
 * Settings sub-nav's alike. React Router's NavLink carries the active
 * state, so neither list needs shared layout state.
 *
 * `collapsed` (the folded sidebar rail, TRA-1085) drops the label to a
 * tooltip and an aria-label, so the icon still names its page. The icon
 * keeps its left inset and the row keeps its height when the label goes,
 * so folding the rail moves nothing.
 */

import { NavLink } from 'react-router-dom'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { NavEntry } from '@/lib/nav'

export function NavEntryLink({
  entry,
  collapsed = false,
}: {
  entry: NavEntry
  collapsed?: boolean
}): React.JSX.Element {
  const { icon: Icon, label, to } = entry
  const link = (
    <NavLink
      to={to}
      // "/" is a prefix of every route — without `end` the Dashboard entry
      // would read active on every page.
      end={to === '/'}
      aria-label={collapsed ? label : undefined}
      // A string, never NavLink's `({ isActive }) => ...` form: the folded
      // rail hands this link to TooltipTrigger's Slot, which string-joins
      // className and would print the function's source as the class list.
      // NavLink sets aria-current="page" on the active route, so the active
      // style keys off that instead. min-h-9 is the labelled row's height
      // (text-sm line plus py-2), held when the label is dropped.
      className="flex min-h-9 w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-normal text-foreground/80 transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring aria-[current=page]:bg-foreground/5 aria-[current=page]:text-foreground"
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && label}
    </NavLink>
  )
  if (!collapsed) return link
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" className="px-2 py-1 text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}
